import {
  memo, useLayoutEffect, useMemo, useRef, useState,
} from 'react';
import { flexRender } from '@tanstack/react-table';
import { useVirtualizer } from '@tanstack/react-virtual';

import VirtualizedTableGroupItem from '../../atoms/VirtualizedTableGroupItem/VirtualizedTableGroupItem';
import TableGroup from '../../atoms/TableGroup/TableGroup';
import TableGroupFooter from '../../atoms/TableGroupFooter/TableGroupFooter';
import TableColGroup from '../../atoms/TableColGroup/TableColGroup';
import TableHead from '../TableHead/TableHead';
import TableBody from '../../atoms/TableBody/TableBody';
import CheckboxHeader from '../../molecules/CheckboxHeader/CheckboxHeader';
import {
  TABLE_ROW_ESTIMATED_SIZE_PX,
  getRowVirtualizerScrollMargin,
  measureTableRowHeightPx,
  roundCssPx,
  shouldVirtualizeTableGroupRows,
} from '../../../domain/table/tableRowVirtualizerUseCase';
import { useTableFindScrollOptional } from '../../../domain/tableFind/useTableFind';

/**
 * Stable cell renderer so TableBodyRow memo is not busted by an inline arrow.
 * @param {{ cell: TableCell }} params
 * @returns {React.ReactNode}
 */
function renderTableGridCell({ cell }) {
  return flexRender(cell.column.columnDef.cell, cell.getContext());
}

/**
 * One virtualized table group (header + body + optional footer). Memoized so
 * toolbar state in TableView (search keystrokes, popovers, mutation pending)
 * does not rebuild 500 rows.
 * `tableWidth` is compared so onChange column-resize still updates cell widths.
 * @param {object} props
 * @param {number} props.dataIndex
 * @param {React.Ref<HTMLDivElement>} props.measureRef
 * @param {number} props.start
 * @param {TableGroupsCustom} props.group
 * @param {TableInstance} props.table
 * @param {number} props.tableWidth
 * @param {boolean} props.isOpen
 * @param {(groupId: string) => void} props.onToggleGroup
 * @param {(group: TableGroupsCustom) => void} props.handleSelectGroupRows
 * @param {boolean} props.isGroupSelected
 * @param {boolean} props.isGroupSelectionIndeterminate
 * @param {(groupId: string, id: string, options?: { shiftKey?: boolean }) => void} props.handleSelectRow
 * @param {import('../../../domain/table/tableReducer').SelectedRowsState} props.selectedRows
 * @param {(statsValue: ApiManagerTableGroupStatValue[]) => TableGroupStatsValue} props.getGroupStatsValueByColumnId
 * @param {TableActiveCell | null} [props.activeCell]
 * @param {React.RefObject<HTMLDivElement | null>} [props.scrollElementRef]
 * @returns {JSX.Element}
 */
function TableGridGroup({
  dataIndex,
  measureRef,
  start,
  group,
  table,
  tableWidth,
  isOpen,
  onToggleGroup,
  handleSelectGroupRows,
  isGroupSelected,
  isGroupSelectionIndeterminate,
  handleSelectRow,
  selectedRows,
  getGroupStatsValueByColumnId,
  activeCell = null,
  scrollElementRef,
}) {
  const headerRef = useRef(/** @type {HTMLDivElement | null} */ (null));
  const [headerHeight, setHeaderHeight] = useState(40);
  const {
    scrollTarget,
    highlightedRowId,
    onScrollTargetConsumed,
  } = useTableFindScrollOptional();

  const groupRows = useMemo(
    () => table.getRowModel().rows.filter((row) => row.original.$groupId === group.id),
    [table, group.id, group.data],
  );
  const virtualizeRows = isOpen && shouldVirtualizeTableGroupRows(groupRows.length);
  const groupHasEditableRows = group.data.some((row) => row.editable);
  const groupFooter = group.statsValue?.length ? (
    <TableGroupFooter
      table={table}
      groupStatsValue={getGroupStatsValueByColumnId(group.statsValue)}
    />
  ) : null;

  useLayoutEffect(() => {
    const el = headerRef.current;
    if (!el) return undefined;
    const update = () => {
      const next = roundCssPx(el.getBoundingClientRect().height) || 40;
      setHeaderHeight((prev) => (prev === next ? prev : next));
    };
    update();
    const observer = new ResizeObserver(update);
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  const bodyScrollMargin = getRowVirtualizerScrollMargin(start, headerHeight);

  const rowVirtualizer = useVirtualizer({
    count: groupRows.length,
    enabled: virtualizeRows,
    getScrollElement: () => scrollElementRef?.current ?? null,
    estimateSize: () => TABLE_ROW_ESTIMATED_SIZE_PX,
    overscan: 8,
    scrollMargin: bodyScrollMargin,
    measureElement: measureTableRowHeightPx,
    getItemKey: (index) => groupRows[index]?.original.id ?? index,
  });

  useLayoutEffect(() => {
    if (!scrollTarget || scrollTarget.groupId !== group.id || !isOpen) return;
    if (virtualizeRows) {
      rowVirtualizer.scrollToIndex(scrollTarget.rowIndexInGroup, { align: 'center' });
    } else {
      const rowEl = scrollElementRef?.current?.querySelector(`[data-row-id="${scrollTarget.rowId}"]`);
      rowEl?.scrollIntoView({ block: 'center' });
    }
    onScrollTargetConsumed();
  }, [
    scrollTarget,
    group.id,
    isOpen,
    virtualizeRows,
    rowVirtualizer,
    scrollElementRef,
    onScrollTargetConsumed,
  ]);

  return (
    <VirtualizedTableGroupItem
      dataIndex={dataIndex}
      measureRef={measureRef}
      start={start}
    >
      <TableGroup
        width={tableWidth}
        title={group.lib}
        isOpen={isOpen}
        onToggle={() => onToggleGroup(group.id)}
        headerRef={headerRef}
        titleAction={groupHasEditableRows ? (
          <CheckboxHeader
            id={`select-all-rows-${group.id}`}
            value={isGroupSelected}
            indeterminate={isGroupSelectionIndeterminate}
            size="small"
            theme="black"
            onChange={() => handleSelectGroupRows(group)}
          />
        ) : null}
        childrenFooter={groupFooter}
      >
        <>
          <TableColGroup table={table} />
          <TableHead
            table={table}
            isVisible={false}
          />
          <TableBody
            table={table}
            rows={groupRows}
            handleSelectRow={handleSelectRow}
            selectedRows={selectedRows}
            activeCell={activeCell}
            rowVirtualizer={virtualizeRows ? rowVirtualizer : null}
            rowVirtualizerScrollMargin={bodyScrollMargin}
            highlightedRowId={highlightedRowId}
          >
            {renderTableGridCell}
          </TableBody>
        </>
      </TableGroup>
    </VirtualizedTableGroupItem>
  );
}

export default memo(TableGridGroup);
